import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { getSuggestedTaxRate } from '@/lib/taxRates';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  clearPendingCreate,
  markStartedOver,
  listPendingCreates,
  loadPendingCreate,
  replayPendingCreate,
  resumePendingCreates,
  type PendingCreate,
  runCreateInvoice,
  isServerError,
  type CreateInvoiceRequest,
  type CreateLineRequest,
  type CreateRunResult,
  type CreateSender,
} from '@/lib/invoice-create-operation';
import type { InvoiceCreateRequest } from '@/lib/schemas';
import { pickPrimaryClientScope } from '@/lib/primaryScope';
import { todayLocalDateString, addLocalDays } from '@/lib/localDate';
import type { ProjectScope, BillableItem } from '@/lib/schemas';
import { useForm, Controller } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProjectCombobox } from '@/components/ProjectCombobox';
import { NewProjectModal } from '@/components/invoices/NewProjectModal';
import { AddScopeModal } from '@/components/scopes/AddScopeModal';
import { toast } from 'sonner';
import { useProjects, useNextInvoiceNumber, useCreateInvoice, useOrganization, useOrganizationEstablishments, useDefaultCurrency, useBillableItems, useInvoiceEstablishmentHint, useInvoiceRecipientPreview } from '@/hooks';
import { RoleBadge } from '@/components/invoices/RoleBadge';
import { ArrowLeft, ArrowRight, FileText, AlertCircle, Loader2 } from 'lucide-react';

// Form validation schema
// Phase 9.19: Simplified - status is always 'draft' for quick create
const invoiceFormSchema = z.object({
  project_id: z.string().min(1, 'Project is required'),
  organization_id: z.string().min(1, 'Client organization is required'),
  client_scope_id: z.string().optional(),
  establishment_id: z.number().int().optional().nullable(),
  invoice_number: z.string().min(1, 'Invoice number is required'),
  invoice_date: z.string().min(1, 'Invoice date is required'),
  due_date: z.string().min(1, 'Due date is required'),
  // Phase 10a.9: Currency
  currency: z.enum(['CAD', 'USD'], { message: 'Please select a currency' }),
});

type InvoiceFormData = z.infer<typeof invoiceFormSchema>;

interface Project {
  id: string;
  project_number: string | number | null;
  name: string;
  client_name?: string;
  // v0.14 Group 9: Multi-client list (primary first) for ProjectCombobox secondary-client search.
  client_names?: string[] | null;
  client_count?: number | null;
}

function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 });
}

const CATEGORY_LABELS: Record<string, string> = {
  professional_fee: 'Professional Fee',
  add_service: 'Add. Service',
  renewal: 'Renewal',
  billable_expense: 'Expense',
  pass_through: 'Pass-Through',
  internal_work: 'Internal',
};

export function InvoiceCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();

  // projectId passed from project page via navigation state
  const sourceProjectId: string | undefined = location.state?.projectId;

  // Fetch projects using authenticated hook
  const { data: projectsData } = useProjects({ limit: 1000, has_project_number: true });

  // Fetch suggested invoice number — track loading to gate submit button
  const {
    data: nextNumberData,
    isLoading: isLoadingInvoiceNumber,
    isError: nextNumberLookupFailed,
  } = useNextInvoiceNumber();

  // Phase 10a.9: Fetch tenant default currency to pre-populate currency selector
  const { data: defaultCurrencyData } = useDefaultCurrency();

  // One create request carries the invoice AND its pre-selected lines (R4, SPEC-christa-contacts-identity).
  const createInvoice = useCreateInvoice();
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? 'unknown-tenant';
  // null: nothing to check. stalled=false: a check is running. stalled=true: the server's answer is
  // not known yet, so the invoice may already exist and she must not press Create again.
  const [checking, setChecking] = useState<{ stalled: boolean } | null>(null);
  // This tab's own unresolved record, so it can still check after another tab resolved it and cleared storage.
  const ownPendingRef = useRef<PendingCreate | null>(null);
  const sendRef = useRef<CreateSender>(async () => {
    throw new Error('Create Invoice is not ready');
  });
  sendRef.current = (request) => createInvoice.mutateAsync(request as unknown as InvoiceCreateRequest);

  const projects: Project[] = projectsData?.projects || [];
  const currencyInitialized = useRef(Boolean(defaultCurrencyData?.default_currency));

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: {
      project_id: '',
      organization_id: '',
      client_scope_id: '',
      establishment_id: null,
      invoice_number: '',
      invoice_date: todayLocalDateString(),
      due_date: addLocalDays(todayLocalDateString(), 30) ?? todayLocalDateString(),
      currency: defaultCurrencyData?.default_currency,
    },
  });

  // Extract watched form values
  const selectedProjectId = watch('project_id');
  const selectedOrganizationId = watch('organization_id');
  const selectedProject = projects.find(p => p.id === selectedProjectId);

  // Track selected billable items to add after invoice creation
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());

  // Reset item selection when project changes
  useEffect(() => {
    setSelectedItemIds(new Set());
  }, [selectedProjectId]);

  // Fetch organisation details for payment terms
  const { data: organizationData } = useOrganization(selectedOrganizationId);
  const selectedOrganization = organizationData?.organization;

  // Fetch establishments (DBAs) for the selected organization
  const { data: establishmentsData } = useOrganizationEstablishments(selectedOrganizationId);
  const establishments = establishmentsData?.establishments || [];

  // R4: Current establishment selection (component-level for hook use)
  const selectedEstablishmentIdForContacts = watch('establishment_id');

  // Fetch unbilled billable items for the selected project (line-item preselect)
  const { data: billableItemsData } = useBillableItems(
    { project_id: selectedProjectId, unbilled_only: true },
    {}
  );
  const availableItems: BillableItem[] = useMemo(
    () => (billableItemsData?.billable_items || []).filter(
      item => !item.cancelled && (item.unbilled_amount ?? item.remaining_amount ?? item.amount) > 0
    ),
    [billableItemsData]
  );

  // Update invoice number when suggestion loads.
  // Invoice numbers are a tenant-wide sequence (e.g. "0042") — they do NOT carry
  // the project's category prefix. See issue #117 for context (Jamie 2026-05-12):
  // adding a prefix like "A-0042" breaks Banks's existing numbering convention.
  // shouldValidate: true clears any pre-existing "required" error.
  useEffect(() => {
    const suggested = nextNumberData?.suggested_invoice_number;
    if (suggested) {
      setValue('invoice_number', suggested, { shouldValidate: true });
    }
  }, [nextNumberData, setValue]);

  // Apply the tenant default once per new invoice. Late settings must not replace
  // an explicit choice, and a background refresh must not reprice an open form.
  useEffect(() => {
    if (defaultCurrencyData?.default_currency && !currencyInitialized.current) {
      currencyInitialized.current = true;
      setValue('currency', defaultCurrencyData.default_currency, { shouldValidate: true });
    }
  }, [defaultCurrencyData, setValue]);

  // Pre-populate project from navigation state
  useEffect(() => {
    const preSelectedProjectId = location.state?.projectId;
    if (preSelectedProjectId && !selectedProjectId) {
      setValue('project_id', preSelectedProjectId);
    }
  }, [location.state, selectedProjectId, setValue]);

  // Fetch project scopes for selected project
  const { data: projectScopesData, isFetched: scopesFetched } = useQuery({
    queryKey: ['project-scopes', selectedProjectId],
    queryFn: () => api.projectScopes.list(selectedProjectId),
    enabled: !!selectedProjectId,
  });

  // R2 (SPEC-bc-pst-tax-engine-2026-08-04, AC10): pre-selected items derive
  // their tax_rate from the TENANT'S CONFIGURED rate (invoice_defaults.
  // tax_rate) via the shared util — not the module constant.
  const { data: invoiceDefaults } = useQuery({
    queryKey: ['settings', 'invoice-defaults'],
    queryFn: async () => {
      try {
        return await api.settings.getInvoiceDefaults();
      } catch {
        return null; // fall back to the module default rate
      }
    },
    staleTime: 5 * 60 * 1000,
  });
  const tenantTaxRate = invoiceDefaults?.tax_rate ?? null;

  const projectOrganizations: ProjectScope[] = useMemo(
    () => projectScopesData?.scopes || [],
    [projectScopesData?.scopes]
  );

  // True only after data has loaded and there are genuinely no client scopes
  const hasNoClientScopes = !!selectedProjectId && scopesFetched &&
    projectOrganizations.filter(scope => scope.is_client_type).length === 0;

  // Auto-select client scope when project is selected
  // Phase 9.28: Originally also set establishment_id from scope here.
  //
  // Hotfix2 — Bug #2 (v0.14): the scope.establishment_id pre-fill was
  // neutralising R1's W2-kill decision tree. R1 explicitly skips when
  // establishment_id is already set (so it doesn't override an explicit
  // user pick), but a stale/corrupted scope.establishment_id (e.g.
  // Brentwood on RTGO from a W2+W6 trap legacy) would slip past R1 because
  // it looks like a "user pick". Result: Christa would still see Brentwood
  // on her new RTGO invoice — defeats the entire W2-kill.
  //
  // Fix: W1 only carries forward organization_id + client_scope_id from
  // the scope. R1's decision tree (is_default → single-establishment →
  // backend hint) is now the canonical truth source for the
  // establishment_id pre-fill.
  const selectedClientScopeId = watch('client_scope_id');
  useEffect(() => {
    if (projectOrganizations.length > 0 && !selectedClientScopeId) {
      const clientScopes = projectOrganizations.filter(scope => scope.is_client_type);
      if (clientScopes.length > 0) {
        // R2 AC9: deterministic primary-scope pick (is_primary DESC, created_at
        // ASC) so this FE auto-pick matches the backend's primary_client_scope_id
        // and the billing-queue client_scopes ordering. Previously this was
        // `find(is_primary) || clientScopes[0]`, where clientScopes[0] depended
        // on get_project_scopes' scope-type display order (non-deterministic with
        // no flagged primary).
        const defaultScope = pickPrimaryClientScope(clientScopes);
        if (!defaultScope) return;
        setValue('client_scope_id', defaultScope.id);
        setValue('organization_id', defaultScope.organization_id);
        // Intentionally NOT setting establishment_id here — see comment above.
        // R1 useEffect below owns the establishment_id decision.
      }
    }
  }, [projectOrganizations, selectedClientScopeId, setValue]);

  // Auto-populate due_date from organization payment terms
  const invoiceDate = watch('invoice_date');
  useEffect(() => {
    if (selectedOrganization?.payment_terms && invoiceDate) {
      const paymentTerms = selectedOrganization.payment_terms;
      const computedDueDate = addLocalDays(invoiceDate, paymentTerms);
      if (computedDueDate) setValue('due_date', computedDueDate);
    }
  }, [selectedOrganization, invoiceDate, setValue]);

  // R1 (v0.14, W2 kill): Smart-default establishment picker.
  // Backend hint endpoint feeds the decision tree below.
  const { data: hintData } = useInvoiceEstablishmentHint({
    projectId: selectedProjectId || undefined,
    clientScopeId: selectedClientScopeId || undefined,
    organizationId: selectedOrganizationId || undefined,
  });

  // Track WHY the establishment was pre-selected so we can render a muted
  // source caption under the picker (the legibility win — silent auto-pick
  // was the W2 complaint; first-class "(from …)" labels fix that).
  // Values: null = no caption; otherwise free-form display string.
  const [establishmentSourceCaption, setEstablishmentSourceCaption] = useState<string | null>(null);
  // Remember the auto-applied id so a later user change can render
  // "Changed from {previous}" instead of the original "From …" caption.
  const [autoSelectedEstablishmentId, setAutoSelectedEstablishmentId] = useState<number | null>(null);
  // Sticky flag: when the user explicitly clears (picks "None"), don't let
  // the decision-tree useEffect re-fill from the smart default. Otherwise
  // single-est would snap back the moment the form value becomes null again.
  const [userClearedEstablishment, setUserClearedEstablishment] = useState(false);
  // R3 (v0.14.5) iteration-2 amendment: tracks whether the user EXPLICITLY
  // interacted with the establishment picker (picked an est OR chose "None").
  // The decision-tree pre-fill (is_default → single → hint) is DISPLAY-ONLY: an
  // untouched picker must NOT emit establishment_id into the create payload, so
  // the backend resolver (resolve_create_establishment_defaults: payload >
  // scope.establishment_id > none) is the authoritative create-time default.
  // payload > scope, so if the FE always sent the displayed hint, UI-created
  // invoices (Christa's flow) would bypass scope inheritance entirely. Only an
  // explicit pick is a payload-worthy "user said so". See R3 output.md.
  const [userInteractedWithEstablishment, setUserInteractedWithEstablishment] = useState(false);
  // When the decision tree programmatically sets the establishment, Radix
  // can echo back a phantom onValueChange with the same value. This ref lets
  // us recognise the next echo and ignore it (it isn't a real user pick).
  const ignoreNextOnValueChangeRef = useRef<number | null>(null);

  // SPEC-christa-contacts-identity R1 AC15: the people shown under Bill To are the server's answer for what
  // the saved invoice will get (project people first, then client defaults, then the last sent invoice), each
  // labelled by the role the server returns. They used to be the client's default contacts with the FIRST
  // called TO, which ignored the project's own saved people (screenshot 12b). The DBA is sent only when she
  // picked one, exactly as the create payload does, so the server decides the same way for both.
  const previewEstablishment =
    userInteractedWithEstablishment && selectedEstablishmentIdForContacts !== null && selectedEstablishmentIdForContacts !== undefined
      ? Number(selectedEstablishmentIdForContacts)
      : undefined;
  const { data: recipientPreview } = useInvoiceRecipientPreview({
    projectId: selectedProjectId || undefined,
    organizationId: selectedOrganizationId || undefined,
    clientScopeId: watch('client_scope_id') || undefined,
    establishmentId: previewEstablishment,
  });
  const previewRecipients = recipientPreview?.recipients ?? [];

  // R1 decision tree. Runs whenever (establishments | hint | org) changes for
  // the current scope. Only fires when there's no current establishment_id
  // selection (i.e. first render or after scope change cleared it) — never
  // overrides a user's explicit pick.
  const selectedEstablishmentId = watch('establishment_id');
  useEffect(() => {
    // Org cleared → wipe selection + caption.
    if (establishments.length === 0) {
      if (selectedEstablishmentId !== null) setValue('establishment_id', null);
      if (establishmentSourceCaption !== null) setEstablishmentSourceCaption(null);
      if (autoSelectedEstablishmentId !== null) setAutoSelectedEstablishmentId(null);
      return;
    }

    // User already has a pick. Don't touch it — onValueChange owns the
    // "Changed from X" caption flip when the user picks a different value.
    if (selectedEstablishmentId !== null && selectedEstablishmentId !== undefined) {
      return;
    }

    // No current pick. If the user explicitly cleared, do NOT re-fill — that
    // would undo their choice. Their "Changed from X" caption stays put.
    if (userClearedEstablishment) {
      return;
    }

    // Otherwise → apply decision tree.
    const orgName =
      selectedOrganization?.name ||
      projectOrganizations.find(s => s.id === selectedClientScopeId)?.organization_name ||
      'this client';

    // NOTE (R3, v0.14.5 iter-2): this decision tree (is_default → single → hint)
    // computes the DISPLAYED pre-fill ONLY. It is a convenience preview — it does
    // NOT decide the create payload. onSubmit emits establishment_id only when the
    // user EXPLICITLY interacts with the picker (see userInteractedWithEstablishment);
    // an untouched picker omits the field so the backend resolver
    // (resolve_create_establishment_defaults: payload > scope.establishment_id >
    // legacy hint) is the authoritative create-time default.
    //
    // Why display intentionally still ignores scope.establishment_id (Bug #2):
    // the Hotfix2 "Bug #2" decision keeps the DISPLAY from surfacing a
    // stale/corrupted scope establishment (W2+W6 trap legacy) — is_default/hint
    // win the visible pre-fill. That protection is preserved here unchanged.
    // The authoritative create-time default now lives server-side regardless, so
    // even when the displayed convenience pick differs from the scope, the saved
    // invoice inherits the scope (the Marine Landing / Christa flow) because the
    // untouched-picker payload omits establishment_id entirely.

    // 1. is_default establishment for the org.
    const defaultEst = establishments.find(e => e.is_default);
    if (defaultEst) {
      ignoreNextOnValueChangeRef.current = defaultEst.id;
      setValue('establishment_id', defaultEst.id);
      setAutoSelectedEstablishmentId(defaultEst.id);
      setEstablishmentSourceCaption(`Default for ${orgName}`);
      return;
    }

    // 2. Single establishment — Jason's amendment: still pre-select for
    // single-est BUT user can de-select via the "None" option in the dropdown.
    if (establishments.length === 1) {
      const only = establishments[0];
      ignoreNextOnValueChangeRef.current = only.id;
      setValue('establishment_id', only.id);
      setAutoSelectedEstablishmentId(only.id);
      setEstablishmentSourceCaption(`Only establishment for ${orgName}`);
      return;
    }

    // 3. Backend hint.
    if (hintData?.suggested_establishment_id != null) {
      const hinted = establishments.find(e => e.id === hintData.suggested_establishment_id);
      if (hinted) {
        ignoreNextOnValueChangeRef.current = hinted.id;
        setValue('establishment_id', hinted.id);
        setAutoSelectedEstablishmentId(hinted.id);
        setEstablishmentSourceCaption(
          hintData.source === 'project_scope_history'
            ? 'From last invoice on this project'
            : 'From last invoice for this client'
        );
        return;
      }
    }

    // 4. No pre-selection — placeholder shows.
    setEstablishmentSourceCaption(null);
    setAutoSelectedEstablishmentId(null);
  }, [
    establishments,
    selectedEstablishmentId,
    hintData,
    selectedOrganization?.name,
    selectedClientScopeId,
    projectOrganizations,
    autoSelectedEstablishmentId,
    establishmentSourceCaption,
    userClearedEstablishment,
    setValue,
  ]);

  // Phase 9.19: Simplified submit — create invoice, optionally add pre-selected items, redirect
  /**
   * 2026-09-26: a blocked submit used to be SILENT.
   *
   * handleSubmit(onSubmit) with no second argument runs nothing when validation
   * fails: no request, no toast, and the only feedback is a small inline
   * message under whichever field is at fault, easy to miss on a form this
   * tall. From the operator's seat the Create Invoice button simply does
   * nothing -- which is exactly how Christa described it ("the create invoice
   * button is not working", and "that has happened on many occasions").
   *
   * Three of the six required fields are populated ASYNCHRONOUSLY
   * (organization_id from projectScopes.list, invoice_number from next-number,
   * due_date from the organization's payment terms), so a slow or failed fetch
   * leaves a required field empty through no action of hers. Say which one.
   */
  const onInvalid = (formErrors: FieldErrors<InvoiceFormData>) => {
    const messages = Object.values(formErrors)
      .map((e) => (e as { message?: string } | undefined)?.message)
      .filter((m): m is string => typeof m === 'string' && m.length > 0);

    toast.error(
      messages.length > 0
        ? messages.join('. ')
        : 'Some required fields are still missing.'
    );
  };

  /** What to do with an outcome: open the invoice, or keep her on a plain "checking" state. */
  const handleCreateOutcome = (result: CreateRunResult, projectId?: string) => {
    if (result.kind === 'created') {
      ownPendingRef.current = null;
      setChecking(null);
      navigate(`/invoices/${result.response.invoice.id}`, { state: { projectId } });
    } else if (result.kind === 'unknown') {
      ownPendingRef.current = result.pending ?? ownPendingRef.current;
      setChecking({ stalled: true });
    } else {
      setChecking(null);
      showServerErrorIfAny(result);
    }
  };

  /** M5: a 500 is shown here, after the one same-key replay found nothing saved (the hook stays quiet so a replay that finds the invoice never shows an error first). */
  const showServerErrorIfAny = (result: CreateRunResult) => {
    if (result.kind === 'refused' && isServerError(result.error)) {
      toast.error('Keystone could not create this invoice. Nothing was saved. Please try again, or use Report Feedback if it keeps happening.');
    }
  };

  /** Replay whatever is pending (same key, same request). Safe to press any number of times. */
  const checkPending = useCallback(async () => {
    setChecking({ stalled: false });
    const own = ownPendingRef.current;
    const result = own
      ? await replayPendingCreate({ tenantId, record: own, send: (r) => sendRef.current(r) })
      : await resumePendingCreates({ tenantId, projectId: selectedProjectId || undefined, send: (r) => sendRef.current(r) });
    if (result.kind === 'created') {
      ownPendingRef.current = null;
      setChecking(null);
      navigate(`/invoices/${result.response.invoice.id}`, {
        state: { projectId: (result.response.invoice as { project_id?: string }).project_id },
      });
    } else if (result.kind === 'unknown') {
      ownPendingRef.current = result.pending ?? ownPendingRef.current;
      setChecking({ stalled: true });
    } else {
      ownPendingRef.current = null;
      setChecking(null);
      showServerErrorIfAny(result);
    }
  }, [tenantId, navigate, selectedProjectId]);

  // The form is tall and the Create button is at the bottom, so bring the checking message into view when it appears.
  const checkingRef = useRef<HTMLDivElement | null>(null);
  const isChecking = checking !== null;
  useEffect(() => {
    const el = checkingRef.current;
    if (isChecking && el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
  }, [isChecking]);

  // A reload or a second tab: an earlier Create Invoice for THIS project may still be unresolved. Replay it with
  // its own key before she can start another. A pending create for a different project never holds this form
  // (it only shows a quiet note below), and nothing pending (the normal case) does nothing.
  const resumedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedProjectId || resumedForRef.current === selectedProjectId) return;
    resumedForRef.current = selectedProjectId;
    if (loadPendingCreate(tenantId, selectedProjectId)) void checkPending();
  }, [tenantId, selectedProjectId, checkPending]);

  // Unresolved creates for other projects: told, never blocked.
  const otherPending = useMemo(
    () => listPendingCreates(tenantId).filter((p) => p.request.project_id !== selectedProjectId),
    // `checking` is a dependency so the note refreshes after a check or a start over changes the stored records.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, selectedProjectId, checking],
  );
  const otherPendingProjectName = otherPending[0]
    ? projects.find((p) => p.id === otherPending[0].request.project_id)?.name ?? 'another project'
    : null;

  /** She looked and it is not there: drop that one record and free the button. Sends nothing. */
  const startOver = () => {
    const projectId = ownPendingRef.current?.request.project_id ?? selectedProjectId;
    if (projectId) {
      clearPendingCreate(tenantId, projectId);
      markStartedOver(projectId);
    }
    ownPendingRef.current = null;
    setChecking(null);
  };

  const onSubmit = async (data: InvoiceFormData) => {
    if (checking) return; // an earlier press is still being checked; never start a second invoice
    try {
      // R3 (v0.14.5) iter-2 amendment: the establishment picker pre-fill is
      // DISPLAY-ONLY. Only emit establishment_id when the user explicitly
      // interacted with the picker. An untouched picker omits the field so the
      // backend resolver (resolve_create_establishment_defaults:
      // payload > scope.establishment_id > legacy hint) computes the
      // authoritative default — otherwise the FE-displayed hint would always
      // land in the payload and, since payload > scope, silently bypass scope
      // inheritance for UI-created invoices (the Marine Landing / Christa flow).
      const payload = { ...data, status: 'draft' as const };
      if (!userInteractedWithEstablishment) {
        // Untouched picker → drop the displayed convenience value so the backend
        // resolver computes the establishment + billing_address_source.
        delete (payload as Partial<typeof payload>).establishment_id;
      }

      // Pre-selected billable items travel IN the create request, priced here. v0.14.4 R1: tax_rate per item
      // comes from its category via the shared util (0% for billable_expense / pass_through); R2 (bc-pst): the
      // revenue-category rate is the tenant's CONFIGURED rate, not a constant.
      const lines: CreateLineRequest[] = availableItems
        .filter((item) => selectedItemIds.has(item.id))
        .map((item) => ({
          billable_item_id: item.id,
          description: item.description,
          amount: item.unbilled_amount ?? item.remaining_amount ?? item.amount,
          tax_rate: getSuggestedTaxRate(item.category, tenantTaxRate),
        }));
      const request: CreateInvoiceRequest = { ...payload, ...(lines.length > 0 ? { lines } : {}) };

      // The runner saves the key and this request BEFORE sending, so a lost answer, a second press, a reload
      // or a second tab can only ever resolve to the one invoice.
      const result = await runCreateInvoice({ tenantId, request, send: (r) => sendRef.current(r) });
      handleCreateOutcome(result, data.project_id);
    } catch (error) {
      console.error('Failed to create invoice:', error);
    }
  };

  const handleCancel = () => {
    if (sourceProjectId) {
      navigate(`/projects/${sourceProjectId}?section=invoices`);
    } else {
      navigate('/invoices');
    }
  };

  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const queryClient = useQueryClient();

  const handleProjectCreated = (project: { id: string; name: string }) => {
    setValue('project_id', project.id);
  };

  const isSubmitting = createInvoice.isPending;

  const selectedTotal = availableItems
    .filter(item => selectedItemIds.has(item.id))
    .reduce((sum, item) => sum + (item.unbilled_amount ?? item.remaining_amount ?? item.amount), 0);

  return (
    <DashboardLayout title="Create Invoice">
      <div className="max-w-2xl">
        {/* Back to project link when navigated from a project page */}
        {sourceProjectId && (
          <button
            type="button"
            onClick={() => navigate(`/projects/${sourceProjectId}?section=invoices`)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {selectedProject ? `Back to ${selectedProject.name}` : 'Back to Project'}
          </button>
        )}

        {/* Info Banner */}
        <div className="mb-6 rounded-lg bg-blue-50 border border-blue-200 p-4">
          <div className="flex gap-3">
            <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Quick Create</p>
              <p className="text-sm text-blue-700">
                Select a project and client to create a draft invoice. You can add line items below or on the next screen.
              </p>
            </div>
          </div>
        </div>

        {checking && (
          <div ref={checkingRef} role="status" aria-live="polite" className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
            <p className="font-medium text-amber-900">Checking whether your invoice was created</p>
            {checking.stalled ? (
              <>
                <p className="mt-1 text-sm text-amber-800">
                  We could not get an answer yet. Your invoice may already be saved, so please do not make another one.
                </p>
                <p className="mt-2 text-sm text-amber-800">
                  If it still says this, open the Invoices list to see whether it was made.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={() => void checkPending()}>
                    Check again
                  </Button>
                  <Button type="button" variant="outline" onClick={() => navigate('/invoices')}>
                    Open the Invoices list
                  </Button>
                  <Button type="button" variant="outline" onClick={startOver}>
                    It is not there, start over
                  </Button>
                </div>
              </>
            ) : (
              <p className="mt-1 text-sm text-amber-800">Please wait. This takes a few seconds.</p>
            )}
          </div>
        )}

        {!checking && otherPending.length > 0 && (
          <div data-testid="other-pending-note" className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            An earlier invoice for {otherPendingProjectName} may not have finished saving. It does not stop you from
            creating this one.{' '}
            <button
              type="button"
              className="underline"
              onClick={() => setValue('project_id', otherPending[0].request.project_id)}
            >
              Check that invoice
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
          {/* Project Selection */}
          <div>
            <Label htmlFor="project_id">Project *</Label>
            <ProjectCombobox
              projects={projects}
              value={watch('project_id')}
              onValueChange={(value) => setValue('project_id', value)}
              placeholder="Select a project"
              searchPlaceholder="Search by number, name, or client..."
              onCreateNew={() => setShowCreateProjectModal(true)}
            />
            {errors.project_id && (
              <p className="text-sm text-red-600 mt-1">{errors.project_id.message}</p>
            )}
          </div>

          {/* Client Scope Selector */}
          <div>
            <Label htmlFor="client_scope_id">Client *</Label>
            {hasNoClientScopes ? (
              <div className="mt-1 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="no-client-scope-warning">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                <span>
                  This project has no client assigned. An invoice needs a client to bill.{' '}
                  <button
                    type="button"
                    onClick={() => setShowAddClientModal(true)}
                    className="font-medium underline underline-offset-2 hover:text-amber-900"
                    data-testid="add-client-scope-link"
                  >
                    Add a client
                  </button>
                  {' '}to continue.
                </span>
              </div>
            ) : (
              <Controller
                name="client_scope_id"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(scopeId) => {
                      const selectedScope = projectOrganizations.find(s => s.id === scopeId);
                      if (selectedScope) {
                        field.onChange(scopeId);
                        setValue('organization_id', selectedScope.organization_id);
                        // R1 (v0.14, W2 kill): when the user manually picks a
                        // different client scope, do NOT auto-fill establishment
                        // from `scope.establishment_id` (Phase 9.28 behavior).
                        // Clear it so the decision-tree useEffect re-runs against
                        // the new scope's hint + is_default + single-est state.
                        setValue('establishment_id', null);
                        setAutoSelectedEstablishmentId(null);
                        setEstablishmentSourceCaption(null);
                        setUserClearedEstablishment(false);
                        // R3 iter-2: a scope switch is not an establishment pick.
                        // Reset interaction so the new scope's create payload omits
                        // establishment_id and the backend resolver re-decides.
                        setUserInteractedWithEstablishment(false);
                      }
                    }}
                  >
                    <SelectTrigger id="client_scope_id">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectOrganizations
                        .filter(scope => scope.is_client_type)
                        .map((scope) => (
                          <SelectItem key={scope.id} value={scope.id}>
                            {scope.organization_name}
                            {scope.is_primary && <span className="ml-2 text-amber-600">(Primary)</span>}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}
              />
            )}
            {errors.organization_id && (
              <p className="text-sm text-red-600 mt-1">{errors.organization_id.message}</p>
            )}
          </div>

          {/* Bill To Summary — shows who receives this invoice at a glance (R4) */}
          {selectedClientScopeId && (() => {
            const clientScope = projectOrganizations.find(scope => scope.id === selectedClientScopeId);
            const orgName = clientScope?.organization_name;
            const estId = watch('establishment_id');
            const est = estId ? establishments.find(e => e.id === estId) : null;
            const address = selectedOrganization?.billing_address || selectedOrganization?.address;

            return (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="bill-to-summary">
                <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-200">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bill To</span>
                </div>
                <div className="px-3 py-2.5 space-y-0.5">
                  <p className="font-semibold text-sm">{orgName}</p>
                  {est && est.name !== orgName && (
                    <p className="text-sm text-blue-700">{est.name}</p>
                  )}
                  {est?.legal_name && (
                    <p className="text-xs text-slate-500">{est.legal_name}</p>
                  )}
                  <div className="border-t border-slate-100 mt-1.5 pt-1.5">
                    {/* R1 AC15: who the saved invoice will go to, by the role the server returns */}
                    {previewRecipients.length > 0 ? (
                      <div className="space-y-0.5" data-testid="bill-to-accounting-contacts">
                        {previewRecipients.map((person) => (
                          <p key={`${person.position}-${person.contact_id ?? person.email ?? person.name}`} className="text-sm">
                            <RoleBadge role={person.role} />{' '}
                            {person.name}
                            {person.email && (
                              <span className="text-xs text-slate-500"> · {person.email}</span>
                            )}
                          </p>
                        ))}
                        <p className="text-xs text-slate-500 pt-0.5" data-testid="bill-to-recipient-source">
                          {recipientPreview?.source === 'project'
                            ? 'Saved for this project'
                            : recipientPreview?.source === 'last_sent'
                              ? 'Copied from the last sent invoice'
                              : 'Client default contacts'}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs text-amber-600" data-testid="bill-to-no-contacts">No contact assigned</p>
                        {recipientPreview && (
                          <p className="text-xs text-slate-500">
                            No contacts saved yet. Add them on the invoice after you create it.
                          </p>
                        )}
                      </div>
                    )}
                    {address ? (
                      <p className="text-xs text-slate-500 mt-1">{address}</p>
                    ) : (
                      <p className="text-xs text-amber-600 mt-1">No address set</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Establishment (DBA) Selector — R1 (v0.14, W2 kill) */}
          {establishments.length > 0 && (
            <div>
              <Label htmlFor="establishment_id">Establishment (DBA)</Label>
              <Controller
                name="establishment_id"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value?.toString() ?? ''}
                    onValueChange={(value) => {
                      // Radix Select can echo back an onValueChange with an
                      // empty string when the controlled value resyncs from
                      // null → number (no real user click). Ignore those —
                      // a real user pick always carries a SelectItem value
                      // ('none' for de-select or a numeric establishment id).
                      if (value === '') {
                        ignoreNextOnValueChangeRef.current = null;
                        return;
                      }

                      const next = value === 'none' ? null : parseInt(value, 10);
                      if (Number.isNaN(next as number)) {
                        // Defensive — should never hit, but if it does, just
                        // drop the event instead of corrupting form state.
                        return;
                      }

                      // Phantom-echo guard: when the decision-tree useEffect
                      // programmatically sets the value, Radix may also echo
                      // back an onValueChange with the same value. Detect via
                      // ref and short-circuit so the caption doesn't flip.
                      if (
                        ignoreNextOnValueChangeRef.current !== null &&
                        next === ignoreNextOnValueChangeRef.current
                      ) {
                        ignoreNextOnValueChangeRef.current = null;
                        field.onChange(next);
                        return;
                      }
                      ignoreNextOnValueChangeRef.current = null;

                      field.onChange(next);

                      // R3 iter-2: this is a genuine user interaction with the
                      // picker (a real est pick OR an explicit "None"). Mark it
                      // so onSubmit EMITS establishment_id into the create payload
                      // (explicit pick wins over the backend scope default). An
                      // untouched picker leaves this false → payload omits the
                      // field → backend resolver decides (scope > legacy hint).
                      setUserInteractedWithEstablishment(true);

                      if (value === 'none') {
                        // Jason's amendment: user explicitly cleared. Set the
                        // sticky flag so the decision tree won't re-fill, and
                        // surface "Changed from X" so the manual choice is
                        // legible.
                        setUserClearedEstablishment(true);
                        if (autoSelectedEstablishmentId !== null) {
                          const previous = establishments.find(
                            e => e.id === autoSelectedEstablishmentId
                          );
                          if (previous) {
                            setEstablishmentSourceCaption(
                              `Changed from ${previous.name}`
                            );
                          } else {
                            setEstablishmentSourceCaption(null);
                          }
                        } else {
                          setEstablishmentSourceCaption(null);
                        }
                      } else {
                        // User picked a real establishment — clear the sticky
                        // flag (subsequent scope changes should re-engage the
                        // decision tree). If this is a manual switch away from
                        // the auto-selected value, surface "Changed from X" so
                        // the picker stays legible.
                        setUserClearedEstablishment(false);
                        if (
                          autoSelectedEstablishmentId !== null &&
                          next !== autoSelectedEstablishmentId
                        ) {
                          const previous = establishments.find(
                            e => e.id === autoSelectedEstablishmentId
                          );
                          if (previous) {
                            setEstablishmentSourceCaption(
                              `Changed from ${previous.name}`
                            );
                          }
                        }
                      }
                    }}
                  >
                    <SelectTrigger id="establishment_id" data-testid="establishment-select">
                      <SelectValue placeholder="Select establishment (optional)" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {/* Always allow de-select — applies to single-est case
                         too (Jason's amendment to the design). User clears via
                         this option; saved value is establishment_id=null. */}
                      <SelectItem value="none">None (use organization name)</SelectItem>
                      {establishments.map((est) => (
                        <SelectItem key={est.id} value={est.id.toString()}>
                          {est.name}
                          {est.is_default && <span className="ml-2 text-muted-foreground">(Default)</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {/* Inline source caption — why this picker is pre-filled.
                 Renders muted-tone immediately below the SelectTrigger so
                 Christa can see "From last invoice on this project" instead
                 of having to guess at the silent auto-pick W2 produced. */}
              {establishmentSourceCaption ? (
                <p
                  className="text-xs text-slate-500 mt-1"
                  data-testid="establishment-source-caption"
                >
                  {establishmentSourceCaption}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  Business name to appear on the invoice
                </p>
              )}
            </div>
          )}

          {/* Invoice Details - Compact Grid */}
          <div className="grid grid-cols-3 gap-4">
            {/* Invoice Number */}
            <div>
              <Label htmlFor="invoice_number">Invoice # *</Label>
              <Input
                id="invoice_number"
                {...register('invoice_number')}
                placeholder={isLoadingInvoiceNumber ? 'Loading…' : '0001'}
                disabled={isLoadingInvoiceNumber}
              />
              {errors.invoice_number && (
                <p className="text-sm text-red-600 mt-1">{errors.invoice_number.message}</p>
              )}
              {/* 2026-09-25: the number lookup gates the Create button, so both
                  of its non-final states have to be visible. Silence here is
                  what made this unreportable: Christa saw a normal-looking form
                  whose button did nothing.

                  On failure the error carries no HTTP status, and
                  handleGlobalQueryError only toasts on >= 500
                  (globalErrorHandlers.ts), so nothing would be said at all. It
                  is said inline rather than as a global toast because that
                  handler fires for every query, background polls included. */}
              {isLoadingInvoiceNumber && (
                <p
                  className="text-sm text-muted-foreground mt-1 flex items-center gap-1"
                  data-testid="next-number-loading"
                >
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Getting the next number…
                </p>
              )}
              {nextNumberLookupFailed && !errors.invoice_number && (
                <p
                  className="text-sm text-amber-700 mt-1 flex items-center gap-1"
                  data-testid="next-number-lookup-failed"
                >
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  Couldn't look up the next number. Enter it manually, or reload to try again.
                </p>
              )}
            </div>

            {/* Invoice Date */}
            <div>
              <Label htmlFor="invoice_date">Invoice Date *</Label>
              <Input
                id="invoice_date"
                type="date"
                {...register('invoice_date')}
              />
              {errors.invoice_date && (
                <p className="text-sm text-red-600 mt-1">{errors.invoice_date.message}</p>
              )}
            </div>

            {/* Due Date */}
            <div>
              <Label htmlFor="due_date">Due Date *</Label>
              <Input
                id="due_date"
                type="date"
                {...register('due_date')}
              />
              {errors.due_date && (
                <p className="text-sm text-red-600 mt-1">{errors.due_date.message}</p>
              )}
            </div>
          </div>

          {/* Phase 10a.9: Currency selector */}
          <div>
            <Label htmlFor="currency">Currency</Label>
            <Controller
              name="currency"
              control={control}
              render={({ field }) => (
                <Select value={field.value ?? ''} onValueChange={(value) => {
                  // Radix's hidden native select can emit an empty value during
                  // initialization. It is not an operator currency choice.
                  if (value !== 'CAD' && value !== 'USD') return;
                  currencyInitialized.current = true;
                  field.onChange(value);
                }}>
                  <SelectTrigger id="currency" className="mt-1" aria-invalid={!!errors.currency} aria-describedby={errors.currency ? 'currency-error' : undefined}>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CAD">CAD — Canadian Dollar</SelectItem>
                    <SelectItem value="USD">USD — US Dollar</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.currency && (
              <p id="currency-error" role="alert" className="text-sm text-red-600 mt-1">{errors.currency.message}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Determines currency symbol and formatting on the PDF.
            </p>
          </div>

          {/* Billable Items — pre-select items to add when invoice is created */}
          {selectedProjectId && availableItems.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line Items <span className="text-muted-foreground font-normal text-sm">(Optional)</span></Label>
                {selectedItemIds.size > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {selectedItemIds.size} selected · {formatCurrency(selectedTotal)}
                  </span>
                )}
              </div>
              <div className="border rounded-lg divide-y">
                {availableItems.map(item => {
                  const unbilledAmt = item.unbilled_amount ?? item.remaining_amount ?? item.amount;
                  const checked = selectedItemIds.has(item.id);
                  return (
                    <label
                      key={item.id}
                      className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(val) => {
                          setSelectedItemIds(prev => {
                            const next = new Set(prev);
                            if (val) next.add(item.id);
                            else next.delete(item.id);
                            return next;
                          });
                        }}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{item.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {CATEGORY_LABELS[item.category] ?? item.category}
                        </p>
                      </div>
                      <span className="text-sm font-medium shrink-0">{formatCurrency(unbilledAmt)}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Select items to include — amounts can be adjusted after creation.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t">
            <Button
              type="submit"
              disabled={isSubmitting || isLoadingInvoiceNumber || checking !== null}
              className="gap-2"
            >
              {isSubmitting ? (
                'Creating…'
              ) : isLoadingInvoiceNumber ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Getting invoice number…
                </>
              ) : (
                <>
                  Create Invoice
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            {checking !== null && (
              <span className="self-center text-sm text-muted-foreground">Waiting for the check to finish.</span>
            )}
          </div>
        </form>
      </div>
      <NewProjectModal
        open={showCreateProjectModal}
        onClose={() => setShowCreateProjectModal(false)}
        onProjectCreated={handleProjectCreated}
      />
      {selectedProjectId && (
        <AddScopeModal
          open={showAddClientModal}
          onOpenChange={setShowAddClientModal}
          projectId={selectedProjectId}
          defaultScopeType="client"
          onScopeAdded={() => {
            queryClient.invalidateQueries({ queryKey: ['project-scopes', selectedProjectId] });
          }}
        />
      )}
    </DashboardLayout>
  );
}
